import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  MapPinIcon,
  HeartIcon,
  TagIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid';

import { useGeolocation, PREFECTURES, calculateDistance } from '../utils/location';
import BannerCarousel from '../components/BannerCarousel';
import JobCountdownTimer from '../components/JobCountdownTimer';
import DateScrollPicker from '../components/DateScrollPicker';
import { API_BASE } from '../lib/config';

const API = API_BASE;

// 勤務時間文字列から時間数を計算するヘルパー関数
const parseWorkHours = (workHours) => {
  if (!workHours) return 0;

  try {
    // "9:00-17:00"や"09:00-17:00"のような形式を想定
    const match = workHours.match(/(\d{1,2}):?(\d{0,2})\s*[-〜~]\s*(\d{1,2}):?(\d{0,2})/);
    if (match) {
      const startHour = parseInt(match[1]);
      const startMin = parseInt(match[2] || '0');
      const endHour = parseInt(match[3]);
      const endMin = parseInt(match[4] || '0');

      const startTime = startHour + startMin / 60;
      const endTime = endHour + endMin / 60;

      return Math.max(0, endTime - startTime);
    }
  } catch (error) {
    console.error('Error parsing work hours:', error);
  }

  return 0;
};

const MobileJobSearchPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { location: userLocation, error: locationError, loading: locationLoading } = useGeolocation();

  const [employers, setEmployers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [expiredJobIds, setExpiredJobIds] = useState(new Set()); // 期限切れ求人のID管理

  // 日付選択ステート
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  // フィルター状態
  const [filters, setFilters] = useState({
    search: '',
    prefecture: '',
    category: '',
    distance: '', // 1km, 5km, 10km, 20km
    sort_by: 'newest' // newest, distance
  });

  // 期限切れ求人を処理する関数
  const handleJobExpired = (jobId) => {
    setExpiredJobIds(prev => new Set([...prev, jobId]));
  };

  useEffect(() => {
    // Guest Access allowed
    // if (!user || user.user_type !== 'job_seeker') {
    //   navigate('/login');
    //   return;
    // }
    fetchCategories();
    fetchEmployers();
    loadFavorites();
  }, [user, navigate]);

  useEffect(() => {
    fetchEmployers();
  }, [filters, userLocation, selectedDate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.category) params.append('category', filters.category);
      if (filters.prefecture) params.append('prefecture', filters.prefecture);

      if (userLocation) {
        params.append('latitude', userLocation.lat);
        params.append('longitude', userLocation.lng);
        // 位置情報連動強化: 半径20km以内を優先検索
        params.append('radius_km', '20');
      }

      if (selectedDate) {
        params.append('work_date', selectedDate);
      }

      // 新しいエンドポイントを使用（代表的な求人情報を含む）
      const response = await axios.get(`${API}/search/employers?${params.toString()}`);
      let filteredEmployers = response.data;

      // 検索フィルタリング
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredEmployers = filteredEmployers.filter(employer =>
          employer.name.toLowerCase().includes(searchTerm) ||
          employer.bio.toLowerCase().includes(searchTerm) ||
          employer.categories.some(cat => cat.toLowerCase().includes(searchTerm)) ||
          employer.location.toLowerCase().includes(searchTerm)
        );
      }

      // 距離フィルタリング
      if (filters.distance && userLocation) {
        const maxDistance = parseInt(filters.distance);
        filteredEmployers = filteredEmployers.filter(employer => {
          if (!employer.distance) return true;
          return employer.distance <= maxDistance;
        });
      }

      // ソート
      if (filters.sort_by === 'distance' && userLocation) {
        filteredEmployers = filteredEmployers.sort((a, b) => {
          if (!a.distance || !b.distance) return 0;
          return a.distance - b.distance;
        });
      } else {
        filteredEmployers.sort((a, b) => {
          if (filters.sort_by === 'newest') {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return 0;
        });
      }

      setEmployers(filteredEmployers);
    } catch (error) {
      toast.error('事業者情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { 'Authorization': `Bearer ${token}` };
      const response = await axios.get(`${API}/employer-favorites`, { headers });
      const favoriteIds = response.data.map(fav => fav.employer_id);
      setFavorites(new Set(favoriteIds));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const toggleFavorite = async (employerId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('ログインしてください');
        return;
      }

      const response = await axios.post(`${API}/employer-favorites/${employerId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newFavorites = new Set(favorites);
      if (response.data.is_favorite) {
        newFavorites.add(employerId);
      } else {
        newFavorites.delete(employerId);
      }

      setFavorites(newFavorites);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('お気に入りの操作に失敗しました');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      prefecture: '',
      category: '',
      distance: '',
      sort_by: 'newest'
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value =>
      value !== '' && value !== 'newest'
    ).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 flex-1 text-center">求人一覧</h1>
            <div className="w-10"></div> {/* 右側のスペーサー */}
          </div>
        </div>
      </div>

      {/* Date Picker - Headerの下にsticky配置 */}
      <div className="sticky top-[53px] z-20 bg-white shadow-sm border-b border-gray-200">
        <DateScrollPicker
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          userLocation={userLocation}
        />
      </div>

      {/* 広告バナーカルーセル */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto">
          <BannerCarousel />
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="px-4 py-3">
        {/* 検索バー */}
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="事業者を検索..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* フィルターボタン */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 relative"
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          フィルター
          {getActiveFiltersCount() > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {getActiveFiltersCount()}
            </span>
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b border-gray-100 p-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  都道府県
                </label>
                <select
                  value={filters.prefecture}
                  onChange={(e) => handleFilterChange('prefecture', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">すべて</option>
                  {PREFECTURES.map(pref => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリ
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">すべて</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  距離
                </label>
                <select
                  value={filters.distance}
                  onChange={(e) => handleFilterChange('distance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">すべて</option>
                  <option value="1">1km以内</option>
                  <option value="5">5km以内</option>
                  <option value="10">10km以内</option>
                  <option value="20">20km以内</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  並び順
                </label>
                <select
                  value={filters.sort_by}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="newest">新着順</option>
                  <option value="distance">近い順</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={clearFilters}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg"
              >
                クリア
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 位置情報エラー */}
      {locationError && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-4 mt-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {locationError}
                <br />
                位置情報を許可すると、近くの事業者を優先して表示できます。
                距離での並び替えが制限される可能性があります。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 位置情報取得成功時の表示 */}
      {userLocation && !locationError && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-4 mt-4">
          <div className="flex">
            <MapPinIcon className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                現在の位置情報を元に、近くの求人事業者を優先表示しています（半径20km以内）
              </p>
            </div>
          </div>
        </div>
      )}

      {/* メインコンテンツ */}
      <div className="flex-1">
        <EmployerCardsList
          employers={employers}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          userLocation={userLocation}
          loading={loading}
          onClearFilters={clearFilters}
          expiredJobIds={expiredJobIds}
          onJobExpired={handleJobExpired}
          selectedDate={selectedDate}
        />
      </div>
    </div>
  );
};

// 事業者カード一覧コンポーネント
const EmployerCardsList = ({ employers, favorites, onToggleFavorite, userLocation, loading, onClearFilters, expiredJobIds, onJobExpired, selectedDate }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 p-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (employers.length === 0) {
    return (
      <div className="text-center py-12">
        <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">事業者が見つかりませんでした</h3>
        <p className="text-gray-600 mb-4">検索条件を変更してみてください</p>
        <button
          onClick={onClearFilters}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          フィルターをクリア
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="text-sm text-gray-600 mb-4">
        {employers.length}件の事業者が見つかりました
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {employers
          .filter(employer => {
            // 期限切れ求人を除外（representative_jobのIDベースで判断）
            if (employer.representative_job?.id) {
              return !expiredJobIds.has(employer.representative_job.id);
            }
            return true;
          })
          .map(employer => (
            <EmployerCard
              key={employer.id}
              employer={employer}
              isFavorite={favorites.has(employer.id)}
              onToggleFavorite={onToggleFavorite}
              userLocation={userLocation}
              onJobExpired={onJobExpired}
              selectedDate={selectedDate}
            />
          ))}
      </div>
    </div>
  );
};

// 事業者カードコンポーネント（ログイン前後で統一デザイン）
const EmployerCard = ({ employer, isFavorite, onToggleFavorite, userLocation, onJobExpired, selectedDate }) => {
  const getDistance = () => {
    if (!userLocation?.lat || !userLocation?.lng) return null;
    if (!employer.distance) return null;

    const distance = employer.distance;
    return distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`;
  };

  // 代表的な求人情報を取得
  const representativeJob = employer.representative_job;
  const hourlyRate = representativeJob?.hourly_rate || employer.avg_hourly_rate;
  const workHours = representativeJob?.work_hours;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      {/* サムネイル画像（ログイン前と同じサイズ: h-32 sm:h-48） */}
      <div className="h-32 sm:h-48 bg-gray-200 relative">
        {employer.thumbnail_images && employer.thumbnail_images.length > 0 ? (
          <img
            src={employer.thumbnail_images[0].startsWith('data:') ? employer.thumbnail_images[0] : `data:image/jpeg;base64,${employer.thumbnail_images[0]}`}
            alt={employer.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center">
            <BuildingOfficeIcon className="w-8 h-8 text-white" />
          </div>
        )}

        {/* お気に入りボタン（ログイン前と同じ位置・サイズ） */}
        <button
          onClick={() => onToggleFavorite(employer.id)}
          className={`absolute top-1 right-1 sm:top-2 sm:right-2 p-1 sm:p-2 rounded-full ${isFavorite
            ? 'bg-red-500 text-white'
            : 'bg-white bg-opacity-80 text-gray-600'
            } hover:bg-opacity-100 transition-all`}
        >
          {isFavorite ? (
            <HeartIconSolid className="h-4 w-4 sm:h-5 sm:w-5" />
          ) : (
            <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          )}
        </button>

        {/* 画像数インジケーター */}
        {employer.thumbnail_images && employer.thumbnail_images.length > 1 && (
          <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-black bg-opacity-50 text-white px-1 sm:px-2 py-1 rounded text-xs">
            {employer.thumbnail_images.length}枚
          </div>
        )}

        {/* カウントダウンタイマー（ログイン前と同じ仕様） */}
        {representativeJob && (
          <JobCountdownTimer
            job={representativeJob}
            onExpired={onJobExpired}
          />
        )}
      </div>

      <div className="p-3 sm:p-4">
        {/* タイトル（ログイン前と同じフォントサイズ） */}
        <h3 className="font-bold text-sm sm:text-lg text-gray-900 mb-1 sm:mb-2 line-clamp-1">
          {representativeJob?.title || employer.name}
        </h3>

        {/* 時給表示（概算給料は削除） */}
        {hourlyRate && (
          <div className="text-sm sm:text-lg font-semibold text-green-600 mb-1 sm:mb-2">
            時給{Math.floor(hourlyRate).toLocaleString()}
          </div>
        )}

        {/* 勤務時間帯（追加要求項目） */}
        {workHours && (
          <div className="flex items-center text-gray-600 text-xs sm:text-sm mb-1">
            <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{workHours}</span>
          </div>
        )}

        {/* 勤務地（距離表示含む） */}
        <div className="flex items-center text-gray-500 text-xs sm:text-sm mb-1 sm:mb-2">
          <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          <span className="truncate">{employer.prefecture} {employer.city}</span>
          {getDistance() && (
            <span className="ml-1 sm:ml-2 bg-blue-100 text-blue-800 px-1 sm:px-2 py-1 rounded text-xs">
              {getDistance()}
            </span>
          )}
        </div>

        {/* カテゴリタグ（ログイン前と同じスタイル） */}
        {employer.categories && employer.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
            {employer.categories.slice(0, 2).map((category, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-700 px-1 sm:px-2 py-1 rounded text-xs"
              >
                {category}
              </span>
            ))}
          </div>
        )}

        {/* 求人件数表示 */}
        {employer.active_jobs_count > 0 && (
          <div className="text-xs sm:text-sm text-gray-600 mb-2">
            求人 {employer.active_jobs_count}件
          </div>
        )}

        {/* 詳細を見るボタン（ログイン前と同じスタイル） */}
        <button
          className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm sm:text-base font-medium hover:bg-blue-600 transition-colors"
          onClick={() => {
            const dateParam = selectedDate ? `?date=${selectedDate}` : '';
            window.location.href = `/employer-profile/${employer.profile_id || employer.id}${dateParam}`;
          }}
        >
          詳細を見る
        </button>
      </div>
    </div>
  );
};

export default MobileJobSearchPage;